import { Squid } from "@0xsquid/sdk";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AxiosError } from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { mainnet } from "viem/chains";
import { Config, createConfig, http, WagmiProvider } from "wagmi";
import { injected } from "wagmi/connectors";
import { squidApiBaseUrl } from "../core/externalLinks";
import { BitcoinProvider } from "../core/providers/BitcoinProvider";
import { CantonProvider } from "../core/providers/CantonProvider";
import { CosmosProvider } from "../core/providers/CosmosProvider";
import { EvmProvider } from "../core/providers/EvmProvider";
import { SolanaProvider } from "../core/providers/SolanaProvider";
import { StellarProvider } from "../core/providers/StellarProvider";
import { SuiProvider } from "../core/providers/SuiProvider";
import { XrplProvider } from "../core/providers/XrplProvider";
import { defaultOptions } from "../core/queries/react-query-config";
import { AppConfig } from "../core/types/config";
import { createWagmiConfig } from "../core/wagmiConfig";
import { useAssetsColorsStore } from "../hooks/store/useAssetsColorsStore";
import {
  useConfigStore,
  useSquidStore,
  useSwapRoutePersistStore,
} from "../hooks/store/useSquidStore";
import {
  fetchAssetsColors,
  HederaExtensionHelper,
  initializeSquidData,
  isEmptyObject,
} from "../services";
import { getConfigWithDefaults } from "../services/internal/configService";

interface ISdkProviderProps {
  children: React.ReactNode;
  config: AppConfig;
  placeholder?: React.ReactNode;
}

const queryClient = new QueryClient({ defaultOptions });

const verifyIntegratorIdValidity = (integratorId: string) => {
  if (!integratorId) {
    throw new Error("Integrator ID is required");
  }
};

export const SquidProvider: React.FC<ISdkProviderProps> = ({
  children,
  config,
  placeholder,
}: ISdkProviderProps) => {
  verifyIntegratorIdValidity(config.integratorId);

  const [wagmiConfig, setWagmiConfig] = useState<Config | undefined>();

  const sdkInitializedRef = useRef(false);

  const initializeSdk = useCallback(async () => {
    if (sdkInitializedRef.current) {
      return;
    }
    sdkInitializedRef.current = true;

    try {
      const squid = new Squid({
        integratorId: config.integratorId,
        baseUrl: config.apiUrl ?? squidApiBaseUrl,
      });

      const [sdkInfoResponse, assetsColorsResponse] = await Promise.allSettled([
        squid.init(),
        fetchAssetsColors(),
      ]);

      if (sdkInfoResponse.status === "rejected") {
        throw sdkInfoResponse.reason;
      }

      if (assetsColorsResponse.status === "fulfilled") {
        useAssetsColorsStore.setState(assetsColorsResponse.value);
      }

      initializeSquidData(
        squid,
        assetsColorsResponse.status === "fulfilled"
          ? assetsColorsResponse.value
          : undefined
      );

      const shouldResetSwapRouteStore =
        // reset swap route if specified in config
        !config?.loadPreviousStateFromLocalStorage ||
        // or if initial assets are provided
        !isEmptyObject(config.initialAssets?.from) ||
        !isEmptyObject(config.initialAssets?.to);

      if (shouldResetSwapRouteStore) {
        useSwapRoutePersistStore.setState({
          swapRoute: undefined,
        });
      }

      useSquidStore.setState((_) => ({
        squid,
        maintenanceMode: { active: false, message: undefined },
      }));

      const hederaExtensions = await HederaExtensionHelper.findExtensions();
      const newWagmiConfig = createWagmiConfig(squid.chains, hederaExtensions);
      setWagmiConfig(newWagmiConfig);

      useConfigStore.setState({
        isInitialized: true,
        config: getConfigWithDefaults(config),
      });
    } catch (error) {
      const isAxios503Error =
        (error as AxiosError).isAxiosError &&
        (error as AxiosError).response?.status === 503;

      const isBackendDown =
        isAxios503Error ||
        (error as AxiosError).isAxiosError ||
        error instanceof Error;

      if (isBackendDown) {
        const maintenanceMessage = isAxios503Error
          ? (((error as AxiosError).response?.data as { message?: string })
              ?.message ?? undefined)
          : "Unable to connect to Squid. Please check your connection or try again later.";

        // Even with an error, we want wagmi to be defined so that we can display the maintenance mode layout
        // Create wagmi config with mainnet as fallback in maintenance mode
        const newWagmiConfig = createConfig({
          ssr: true,
          chains: [mainnet],
          connectors: [injected()],
          transports: {
            [mainnet.id]: http(),
          },
        });
        setWagmiConfig(newWagmiConfig);

        useConfigStore.setState({
          isInitialized: false,
          config: getConfigWithDefaults(config),
        });

        useSquidStore.setState({
          squid: undefined,
          maintenanceMode: {
            active: true,
            message: maintenanceMessage,
          },
        });
      } else {
        console.error("Error initializing SDK:", error);
      }
    }
  }, [config]);

  useEffect(() => {
    initializeSdk();
  }, [initializeSdk]);

  return wagmiConfig ? (
    <WagmiProvider reconnectOnMount={false} config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <CantonProvider>
          <StellarProvider>
            <EvmProvider>
              <XrplProvider>
                <SuiProvider>
                  <SolanaProvider>
                    <BitcoinProvider>
                      <CosmosProvider>{children}</CosmosProvider>
                    </BitcoinProvider>
                  </SolanaProvider>
                </SuiProvider>
              </XrplProvider>
            </EvmProvider>
          </StellarProvider>
        </CantonProvider>
      </QueryClientProvider>
    </WagmiProvider>
  ) : (
    placeholder
  );
};
